import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { BLOG_QUERY } from "../graphql/blogs";
import {
  Box,
  Container,
  Typography,
  Divider,
  Stack,
  Button,
} from "@mui/material";
import { buildImageUrl } from "../config";

const formatDate = (iso?: string | null) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "2-digit",
    });
  } catch {
    return "";
  }
};

const asHtml = (content?: string | null) => {
  const raw = (content || "").trim();
  if (!raw) return "";

  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
  if (looksLikeHtml) return raw;

  const escape = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const safe = escape(raw)
    .replace(/\r\n/g, "\n")
    .replace(/\n{2,}/g, "</p><p>")
    .replace(/\n/g, "<br/>");

  return `<p>${safe}</p>`;
};

export default function BlogDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const { data, loading } = useQuery(BLOG_QUERY, {
    variables: { slug },
  });

  const blog = data?.blog;
  if (!blog && !loading) return null;

  return (
    <Box sx={{ py: { xs: 6, md: 9 } }}>
      <Container maxWidth="lg">
        {/* Back */}
        <Button
          onClick={() => navigate(-1)}
          sx={{
            mb: 3,
            pl: 0,
            color: "text.secondary",
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          ← Back to Journal
        </Button>

        {/* Title Section */}
        <Stack spacing={2} sx={{ maxWidth: 820, mx: "auto", mb: 5 }}>
          <Typography
            variant="overline"
            sx={{ letterSpacing: 2, color: "text.secondary" }}
          >
            Journal
          </Typography>

          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              lineHeight: 1.15,
            }}
          >
            {blog?.title}
          </Typography>

          <Typography
            sx={{
              color: "text.secondary",
              fontSize: 18,
            }}
          >
            {blog?.excerpt}
          </Typography>

          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {formatDate(blog?.publishedAt || blog?.createdAt)}
          </Typography>
        </Stack>

        {/* Hero Image */}
        {blog?.coverImage && (
          <Box
            sx={{
              maxWidth: 1100,
              mx: "auto",
              mb: 6,
            }}
          >
            <Box
              component="img"
              loading="lazy"
              decoding="async"
              src={buildImageUrl(blog.coverImage)}
              alt={blog.title}
              sx={{
                width: "100%",
                maxHeight: 520,
                objectFit: "cover",
                borderRadius: 0,
              }}
            />
          </Box>
        )}

        {/* Content */}
        <Container maxWidth="md">
          <Divider sx={{ mb: 4 }} />

          <Box
            sx={{
              typography: "body1",
              fontSize: 17,
              lineHeight: 1.9,
              color: "text.primary",
              overflowWrap: "anywhere",
              wordBreak: "break-word",

              "& p": {
                mb: 3,
              },

              "& h2": {
                mt: 6,
                mb: 2,
                fontSize: 28,
                fontWeight: 700,
              },

              "& h3": {
                mt: 5,
                mb: 2,
                fontSize: 22,
                fontWeight: 700,
              },

              "& img": {
                maxWidth: "100%",
                height: "auto",
                margin: "32px 0",
              },

              "& blockquote": {
                margin: "32px 0",
                paddingLeft: 24,
                borderLeft: "3px solid",
                borderColor: "divider",
                color: "text.secondary",
                fontStyle: "italic",
              },

              "& a, & li, & span, & div": {
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              },

              "& pre, & code": {
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              },
            }}
            dangerouslySetInnerHTML={{ __html: asHtml(blog?.content) }}
          />

          <Divider sx={{ my: 6 }} />

          {/* Footer */}
          <Stack alignItems="center" spacing={1}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Thanks for reading
            </Typography>

            <Button
              onClick={() => navigate("/blogs")}
              sx={{
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Back to Journal →
            </Button>
          </Stack>
        </Container>
      </Container>
    </Box>
  );
}
